import * as Yup from 'yup';
import { date, string, TypeOf } from 'yup';
import dayjs from 'dayjs';
import {
    BESTELLMENGE_KOMMA_ERROR,
    KONTRAKT_VALIDATION_ERROR,
    NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR,
    NEWID_PREFIX,
    WERK_VALIDATION_ERROR,
    WUNSCHTERMIN_VALIDATION_ERRORS,
    ZULAGE_VALIDATION_ERROR,
    SILO_VALIDATION_ERROR,
} from '../../constants';
import { Berechnungsart } from '../../types/enums';
import { testLogischerWunschterminzeitraum } from './testFunctions';
import { selectSiloValidation } from '../../../store/SiloValidation.store';
import { store } from '../../../configureStore';
import { minDate } from '../../helper/date-helper';

const mengeSchema = Yup.string().matches(/^\d*(?:,\d)?$/, BESTELLMENGE_KOMMA_ERROR);

const kontraktSchema = Yup.string().when(['berechnungsart'], {
    is: (berechnungsart: Berechnungsart) => {
        return berechnungsart === Berechnungsart.Kontrakt;
    },
    then: Yup.string().required(KONTRAKT_VALIDATION_ERROR),
    otherwise: (schema) => schema.nullable(),
});

const bezeichnungSchema = Yup.string().when(['nummer'], {
    is: (nummer: string) => nummer && nummer.startsWith(NEWID_PREFIX),
    then: Yup.string().required(NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR),
});

const mengePalSchema = Yup.string()
    .matches(/^\d*(?:,\d)?$/, ' ')
    .nullable()
    .optional();
const mengeVeSchema = Yup.string()
    .matches(/^\d*(?:,\d)?$/, ' ')
    .nullable()
    .optional();

const werkIdSchema = Yup.string().when(['menge', 'mengeVe', 'mengePal'], {
    is: (menge: number | string, mengeVe: number | string, mengePal: number | string) =>
        Boolean(menge) || Boolean(mengeVe) || Boolean(mengePal),
    then: Yup.string().required(WERK_VALIDATION_ERROR),
});

/**
 * Nur echte Sonderherstellung (Freitext „Zulage (*)“ / Legacy): zulageText Pflicht.
 * Artikelzulage per Dropdown: keine Pflicht zur Freitext-Meldung, wenn nichts gewählt (zulageHatArtikelDropdown).
 */
const zulageTextSchema = Yup.string().test('zulage-sonderherstellung-freitext', ZULAGE_VALIDATION_ERROR, function (zulageText) {
    const parent = this.parent as {
        zulage?: boolean;
        zulageId?: string;
        zulagenData?: { zulagen?: Array<{ zulageId?: string; freitext?: string }> };
        zulageHatArtikelDropdown?: boolean;
    };
    const { zulage, zulageId, zulagenData, zulageHatArtikelDropdown } = parent;
    if (!zulage) return true;
    if (zulageId) return true;
    if (zulageHatArtikelDropdown === true) return true;
    const zulagen = zulagenData?.zulagen;
    const hasStructuredSelection = zulagen?.some(
        (z) => (z.zulageId != null && String(z.zulageId).trim() !== '') || Boolean(z.freitext?.trim())
    );
    if (hasStructuredSelection) return true;
    const trimmed = zulageText != null ? String(zulageText).trim() : '';
    return trimmed.length > 0;
});

/** Bis-Datum nur prüfen (>= Von), wenn Von-Datum gültig ist (angegeben und nicht in der Vergangenheit). */
export const datumWEBisSchema = date().when('datumWEVon', (datumWEVon: string) => {
    if (!datumWEVon) return date();
    if (dayjs(datumWEVon).isBefore(dayjs(minDate), 'day')) return date();
    return date().min(datumWEVon, WUNSCHTERMIN_VALIDATION_ERRORS.DATUM_VON_IS_AFTER_DATUM_BIS);
});

export const uhrzeitWEBisSchema = string().test(
    'logischerWunschterminzeitraum',
    WUNSCHTERMIN_VALIDATION_ERRORS.TIME_VON_IS_AFTER_TIME_BIS,
    testLogischerWunschterminzeitraum
);

export const datumWEBisSchemaLoose = date();

export const uhrzeitWEBisSchemaLoose = string();

const siloSchema = Yup.string()
    .transform((value) => (value === null ? undefined : value))
    .test('forbidden-silo', SILO_VALIDATION_ERROR, (value) => {
        const { silosConfirmed } = selectSiloValidation(store.getState());
        if (silosConfirmed) {
            return true;
        }

        if (!value) return true;
        const forbiddenRegex = /(&|\+|und)/i;
        return !forbiddenRegex.test(value);
    });

export const bestellpositionViewBaseSchema = Yup.object({
    bezeichnung: bezeichnungSchema,
    kontrakt: kontraktSchema,
    menge: mengeSchema,
    mengePal: mengePalSchema,
    mengeVe: mengeVeSchema,
    werkId: werkIdSchema,
    zulageText: zulageTextSchema,
    silo: siloSchema,
});

export type BestellpositionViewTypeShape = TypeOf<typeof bestellpositionViewBaseSchema>;
